import { LitElement, html } from 'lit';
import { nothing } from 'lit-html';
import fsTypography from '@cof/fs-typography-core/lib/typography.lit.min.js';
import { TagKey } from '@cof/tag-catalog-fs-vehicle-and-loan-mfe';
import { style } from './iconic-vehicle-lookup.style.js';
import { DebugService } from '../../../shared/services/debug.service.js';
import { REFI_VEHICLE_LOAN_CONST } from '../../../shared/constants/app.constants.js';
import { VehicleService } from '../../../services/vehicle.service.js';
import { stratum } from '../../../../constants/stratum.js';
import '../iconic-vin-inputs/iconic-vin-inputs.component.js';
import '../iconic-license-plate-inputs/iconic-license-plate-inputs.component.js';
import { tags } from '../../../shared/constants/tags.js';
import { ApplicationService } from '../../../services/application.service.js';
import '@cof/fs-alert-wc/fs-alert.js';

class RefiIconicVehicleLookup extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiIconicVehicleLookup');
    this.inputsUsed =
      VehicleService.lookupType ===
      REFI_VEHICLE_LOAN_CONST.VEHICLE_LOOKUP_TYPE.SSN
        ? REFI_VEHICLE_LOAN_CONST.VEHICLE_LOOKUP_TYPE.LICENSE_PLATE
        : VehicleService.lookupType;
    this.loading = false;
    this.inputs = [
      {
        label: 'License Plate',
        ariaControlsId: 'license-tab',
        id: 'license-label',
        tabValue: REFI_VEHICLE_LOAN_CONST.VEHICLE_LOOKUP_TYPE.LICENSE_PLATE,
      },
      {
        label: 'VIN',
        ariaControlsId: 'vin-tab',
        id: 'vin-label',
        tabValue: REFI_VEHICLE_LOAN_CONST.VEHICLE_LOOKUP_TYPE.VIN,
      },
    ];
    this.validity = {
      licensePlateNumber: false,
      licensePlateStateCode: false,
      vin: false,
    };
    this.vehicleLookupModel = {};
    this.marketingOffer = ApplicationService.MarketingOffer;
  }

  firstUpdated() {
    if (ApplicationService.isInAppDeclineDelayOn) {
      stratum.publishTag(
        TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_VEHICLE_LOAN_LOOKUP_2
      );
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.vehicleLookupModel = VehicleService.vehicleLookupModel;
    this.validity.licensePlateNumber =
      !!this.vehicleLookupModel.licensePlateNumber;
    this.validity.licensePlateStateCode =
      !!this.vehicleLookupModel.residenceStateCode;
    this.validity.vin = !!this.vehicleLookupModel.vin;
    this.debug.log('vehicle lookup model', this.vehicleLookupModel);
  }

  static properties = {
    inputsUsed: { type: String, attribute: false },
    loading: { type: Boolean, attribute: false },
  };

  static styles = [style, fsTypography];

  async lookupVehicle(e) {
    e.preventDefault();
    this.debug.log('looking up vehicle');
    if (this.verifyValidity(this.inputsUsed)) {
      REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
        tags.shared.vehicle.lookup.continue(this.inputsUsed)
      );

      this.toggleErrorAndLoading(false, '');
      this.dispatchEvent(
        new CustomEvent('start-animation', { bubbles: true, composed: true })
      );
      await VehicleService.vehicleLookup(
        this.vehicleLookupModel,
        this.inputsUsed
      ).then(
        (res) => {
          this.debug.log('vehicle lookup call successful', res);
          if (
            this.inputsUsed ===
            REFI_VEHICLE_LOAN_CONST.VEHICLE_LOOKUP_TYPE.LICENSE_PLATE
          ) {
            REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
              tags.shared.vehicle.lookup.plateSuccessCarfax,
              '',
              'api',
              'message'
            );
            stratum.publishTag(
              ApplicationService.isInAppDeclineDelayOn
                ? TagKey.VEHICLE_LICENSE_PLATE_LOOKUP_CARFAX_LICENSE_PLATE_LOOKUP_SUCCESS
                : TagKey.VEHICLE_LICENSE_PLATE_LOOKUP_LICENSE_PLATE_SUCCESS_CARFAX
            );
          } else {
            REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
              'vin success nada',
              '',
              'api',
              'message'
            );
            stratum.publishTag(
              ApplicationService.isInAppDeclineDelayOn
                ? TagKey.VEHICLE_VIN_LOOKUP_NADA_VIN_LOOKUP_SUCCESS
                : TagKey.VEHICLE_VIN_LOOKUP_VIN_SUCCESS_NADA
            );
          }
          this.loading = false;
          this.dispatchEvent(new CustomEvent('found-vehicles'));
          this.dispatchEvent(
            new CustomEvent('host-app-event', {
              bubbles: true,
              composed: true,
              detail: {
                actionName:
                  REFI_VEHICLE_LOAN_CONST.hostAppEventActionName
                    .initiateAnimationCompletion,
                data: {
                  showCompletionAnimation: true,
                  successAnimationLength: 1500,
                  skipCurtainCloseAnimation: false,
                },
              },
            })
          );
        },
        (err) => {
          if (ApplicationService.isInAppDeclineDelayOn) {
            if (
              this.inputsUsed ===
              REFI_VEHICLE_LOAN_CONST.VEHICLE_LOOKUP_TYPE.LICENSE_PLATE
            ) {
              stratum.publishTag(TagKey.VIEW_VEHICLE_LICENSE_PLATE_LOOKUP_2);
              stratum.publishTag(
                TagKey.VEHICLE_LICENSE_PLATE_LOOKUP_CARFAX_LICENSE_PLATE_LOOKUP_ERROR
              );
            } else {
              stratum.publishTag(TagKey.VIEW_VEHICLE_VIN_LOOKUP_4);
              stratum.publishTag(
                TagKey.VEHICLE_VIN_LOOKUP_NADA_VIN_LOOKUP_ERROR
              );
            }
          } else if (err.message === 'no vehicles') {
            REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
              tags.shared.vehicle.lookup.notFound,
              '',
              'error',
              'message'
            );
            if (
              this.inputsUsed ===
              REFI_VEHICLE_LOAN_CONST.VEHICLE_LOOKUP_TYPE.LICENSE_PLATE
            ) {
              stratum.publishTag(TagKey.VIEW_VEHICLE_LICENSE_PLATE_LOOKUP);
            } else {
              stratum.publishTag(TagKey.VIEW_VEHICLE_VIN_LOOKUP_2);
            }

            const errorMessage = `We weren’t able to find a car with the info entered. Please ensure the info entered is correct or try again later.`;
            this.toggleErrorAndLoading(true, errorMessage);
            this.sendPrequalInfoToSDP({
              ...err,
              errorMessage: `${errorMessage}`,
            });
            this.dispatchEvent(
              new CustomEvent('host-app-event', {
                bubbles: true,
                composed: true,
                detail: {
                  actionName:
                    REFI_VEHICLE_LOAN_CONST.hostAppEventActionName
                      .initiateAnimationCompletion,
                  data: {
                    showCompletionAnimation: false,
                    skipCurtainCloseAnimation: false,
                  },
                },
              })
            );
            return;
          } else if (
            this.inputsUsed ===
            REFI_VEHICLE_LOAN_CONST.VEHICLE_LOOKUP_TYPE.LICENSE_PLATE
          ) {
            REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
              tags.shared.vehicle.lookup.carfaxDown,
              '',
              'api',
              'message'
            );
            stratum.publishTag(
              TagKey.VEHICLE_LICENSE_PLATE_LOOKUP_CARFAX_API_DOWN_ERROR
            );
          } else {
            REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
              tags.shared.vehicle.lookup.nadaDown,
              '',
              'api',
              'message'
            );
            stratum.publishTag(TagKey.VEHICLE_VIN_LOOKUP_NADA_API_DOWN_ERROR);
          }
          const errMessage = `We are unable to find your vehicle, please try again later.`;
          this.toggleErrorAndLoading(true, errMessage);
          this.sendPrequalInfoToSDP({
            ...err,
            errorMessage: `${errMessage}`,
          });
          this.dispatchEvent(
            new CustomEvent('host-app-event', {
              bubbles: true,
              composed: true,
              detail: {
                actionName:
                  REFI_VEHICLE_LOAN_CONST.hostAppEventActionName
                    .initiateAnimationCompletion,
                data: {
                  showCompletionAnimation: false,
                  skipCurtainCloseAnimation: false,
                },
              },
            })
          );
        }
      );
    } else {
      this.debug.log('vehicle lookup not submitted due to invalid form');
    }
  }

  sendPrequalInfoToSDP(err) {
    VehicleService.sendVehiclePrequalInfoToSDP(
      this.vehicleLookupModel,
      false,
      err.errorMessage
    );
  }

  verifyValidity(inputType) {
    this.debug.log('verifying validity for input', inputType);
    switch (inputType) {
      case REFI_VEHICLE_LOAN_CONST.VEHICLE_LOOKUP_TYPE.LICENSE_PLATE:
        return (
          this.validity.licensePlateNumber &&
          this.validity.licensePlateStateCode
        );
      case REFI_VEHICLE_LOAN_CONST.VEHICLE_LOOKUP_TYPE.VIN:
        return this.validity.vin;
      default:
        return false;
    }
  }

  toggleErrorAndLoading(showErr, errMessage) {
    this.showError = showErr;
    this.loading = !showErr;
    this.errorMessage = errMessage;
  }

  handleTabChange(event) {
    this.debug.log('instead changing input to', event.target.value);
    this.inputsUsed = event.target.value;
    this.validity[this.inputsUsed] = false;
  }

  changed(ev) {
    const { name, value } = ev.detail;
    this.vehicleLookupModel[name] = value;
  }

  updateValidity(ev) {
    this.debug.log(
      `${ev.detail.name} field is ${ev.detail.isValid ? 'valid' : 'invalid'}`
    );
    this.validity[ev.detail.name] = ev.detail.isValid;
  }

  render() {
    this.debug.log('rendered vehicle-lookup');
    return html`
      <refi-track-pageview pagename="vehicle lookup"></refi-track-pageview>
      ${this.showError && ApplicationService.isInAppDeclineDelayOn
        ? html`<refi-error-message-val
            errorHeading="${this.errorHeading}"
            errorMessage="${this.errorMessage}"
          ></refi-error-message-val>`
        : ''}
      <form
        name="vehLookupForm"
        @submit="${this.lookupVehicle}"
        novalidate
        autocomplete="off"
      >
        <div>
          <div class="button-toggle-group vehicle-lookup__tab-button">
            <fs-choice-button-group data-e2e-ref="btnVehicleToggle">
              ${this.inputs.map(
                (input) => html`
                  <fs-choice-button
                    name="${input.id}"
                    alignment="center"
                    label-text="${input.label}"
                    type="radio"
                    value="${input.tabValue}"
                    @click="${this.handleTabChange}"
                    selected="${input.tabValue === this.inputsUsed
                      ? true
                      : nothing}"
                    data-e2e-ref="${input.tabValue}ChoiceButton"
                  ></fs-choice-button>
                `
              )}
            </fs-choice-button-group>
          </div>
          ${this.inputsUsed ===
          REFI_VEHICLE_LOAN_CONST.VEHICLE_LOOKUP_TYPE.LICENSE_PLATE
            ? html`<refi-iconic-license-plate-inputs
                id="license-tab"
                @validity="${this.updateValidity}"
                .licensePlate="${this.vehicleLookupModel.licensePlateNumber}"
                .licensePlateState="${this.vehicleLookupModel
                  .licensePlateStateCode}"
              ></refi-iconic-license-plate-inputs>`
            : html`<refi-iconic-vin-inputs
                id="vin-tab"
                @validity="${this.updateValidity}"
                @changed="${this.changed}"
              ></refi-iconic-vin-inputs>`}
          ${this.showError && !ApplicationService.isInAppDeclineDelayOn
            ? html`<fs-alert
                class="alert"
                type="error"
                role="alert"
                aria-atomic="true"
                data-e2e-error="error-message"
                >${this.errorMessage}</fs-alert
              >`
            : ''}
          <div class="search-button-container">
            ${!this.loading
              ? html`<fs-button
                  class="card-button"
                  variant="primary"
                  data-e2e-ref="btnVehicleLookup"
                  >Search</fs-button
                >`
              : html`<fs-button
                  variant="primary"
                  loading
                  disabled
                  style="min-width:91.883px;"
                  >Search</fs-button
                >`}
          </div>
        </div>
      </form>
    `;
  }
}

customElements.define('refi-iconic-vehicle-lookup', RefiIconicVehicleLookup);
